import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import {
  RabbitStompConfig,
  IRabbitStompService,
  RabbitStompEventHandlers,
} from './rabbit-stomp.types';

class RabbitStompService implements IRabbitStompService {
  private static instance: RabbitStompService | null = null;
  private client: Client | null = null;
  private config: RabbitStompConfig | null = null;
  private isConnecting: boolean = false;
  private connectionState: boolean = false;
  private lastReportedState: boolean = false;
  private healthCheckInterval: ReturnType<typeof setInterval> | null = null;
  private eventHandlers: RabbitStompEventHandlers = {
    connect: [],
    disconnect: [],
    error: [],
  };

  private constructor() {
    // Private constructor para garantir singleton
  }

  public static getInstance(config?: RabbitStompConfig): RabbitStompService {
    if (!RabbitStompService.instance) {
      RabbitStompService.instance = new RabbitStompService();
    }

    if (config) {
      RabbitStompService.instance.initialize(config);
    }

    return RabbitStompService.instance;
  }

  public static resetInstance(): void {
    if (RabbitStompService.instance) {
      RabbitStompService.instance.disconnect();
      RabbitStompService.instance = null;
    }
  }

  private initialize(config: RabbitStompConfig): void {
    if (this.client) {
      this.disconnect();
    }

    this.config = config;
    this.setupClient();
  }

  private setupClient(): void {
    if (!this.config) {
      throw new Error('Configuração não fornecida');
    }

    const { brokerUrl, username, password, port, vhost } = this.config;

    if (!brokerUrl || !username || !password) {
      const error = 'Configuração STOMP incompleta. Verifique brokerUrl, username e password.';
      this.emitEvent('error', error);
      throw new Error(error);
    }

    const wsPort = port || 15674;
    const wsProtocol = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsUrl = `${wsProtocol}://${brokerUrl}${port ? `:${wsPort}` : ''}/ws`;
    try {
    this.client = new Client({
      brokerURL: wsUrl,
      connectHeaders: {
        login: username,
        passcode: password,
        host: vhost || '/',
      },
      debug: (str) => {
        if (this.config?.debug) {
        }
      },
      reconnectDelay: this.config.reconnectDelay || 3000,
      heartbeatIncoming: this.config.heartbeatIncoming || 20000,
      heartbeatOutgoing: this.config.heartbeatOutgoing || 20000,
      splitLargeFrames: true,
        forceBinaryWSFrames: false,
        appendMissingNULLonIncoming: true,
      });
    } catch (error) {
      console.error('[RabbitStompService] Erro ao criar cliente STOMP:', error);
      throw error;
    }

    this.setupEventHandlers();
  }

  private setupEventHandlers(): void {
    if (!this.client) return;

    this.client.onConnect = (frame) => {
      this.connectionState = true;
      this.isConnecting = false;
      this.startHealthCheck();
      this.emitEvent('connect');
    };

    this.client.onStompError = (frame) => {
      const errorMsg = frame.headers['message'] || 'Erro desconhecido';
      console.error('[RabbitStompService] ❌ Erro STOMP:', errorMsg);
      this.connectionState = false;
      this.isConnecting = false;
      this.emitEvent('error', errorMsg);
    };

    this.client.onWebSocketError = (event) => {
      console.error('[RabbitStompService] ❌ Erro WebSocket:', event);
      this.connectionState = false;
      this.isConnecting = false;
      this.emitEvent('error', 'Erro de conexão WebSocket');
    };

    this.client.onDisconnect = (frame) => {
      this.connectionState = false;
      this.isConnecting = false;
      this.stopHealthCheck();
      this.emitEvent('disconnect');

      // Reconectar se não foi uma desconexão intencional
      if (!frame || frame.command !== 'DISCONNECT') {
        setTimeout(() => {
          if (this.client && !this.client.connected) {
            this.connect();
          }
        }, 2000);
      }
    };

    this.client.onWebSocketClose = (event) => {
      this.connectionState = false;
      this.isConnecting = false;

      // Reconectar se não foi fechamento limpo
      if (!event.wasClean) {
        setTimeout(() => {
          if (this.client && !this.client.connected) {
            this.connect();
          }
        }, 3000);
      }
    };
  }

  private startHealthCheck(): void {
    this.stopHealthCheck();

    this.healthCheckInterval = setInterval(() => {
      if (this.client && !this.client.connected) {
        this.connectionState = false;
        this.connect();
      }
    }, 30000); // Verifica a cada 30 segundos
  }

  private stopHealthCheck(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }

  private emitEvent(event: keyof RabbitStompEventHandlers, ...args: any[]): void {
    const handlers = this.eventHandlers[event];
    handlers.forEach((handler) => {
      try {
        handler(...args);
      } catch (error) {
        console.error(`[RabbitStompService] Erro ao executar handler do evento ${event}:`, error);
      }
    });
  }

  public connect(): void {
    if (this.isConnecting) {
      return;
    }

    if (this.client?.connected) {
      return;
    }

    if (!this.client) {
      throw new Error('Cliente STOMP não foi inicializado. Configure primeiro.');
    }

    this.isConnecting = true;
    this.client.activate();
  }

  public disconnect(): void {
    this.stopHealthCheck();
    if (this.client) {
      try {
        this.client.deactivate();
      } catch (error) {
      }
      this.client = null;
    }

    this.connectionState = false;
    this.isConnecting = false;
  }

  public isConnected(): boolean {
    const result = this.connectionState && this.client?.connected === true;
    this.lastReportedState = result;
    return result;
  }

  public async sendToQueue(message: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.client || !this.isConnected()) {
        reject(new Error('Conexão STOMP não está disponível'));
        return;
      }

      try {
        let destination: string;
        const queueName = this.config?.queue || 'actions.ingest.q';
        // Configuração especial para fila do mike-goldberg-v2
        if (queueName === 'actions.ingest.q') {
          destination = `/exchange/actions.ingest/actions.ingest.q`;
        } else {
          destination = `/queue/${queueName}`;
        }

        this.client.publish({
          destination,
          body: message,
          headers: {
            'content-type': 'text/plain',
            persistent: 'true',
          },
        });

        resolve();
      } catch (error) {
        console.error('[RabbitStompService] Erro ao enviar mensagem:', error);
        reject(error);
      }
    });
  }

  public subscribeToQueue(
    queueName: string,
    callback: (message: string) => void
  ): StompSubscription {
    if (!this.client || !this.isConnected()) {
      throw new Error('Conexão STOMP não está disponível');
    }

    const destination = `/queue/${queueName}`;

    return this.client.subscribe(destination, (message: IMessage) => {
      callback(message.body);
    });
  }

  public subscribeToTopic(
    topicPattern: string,
    callback: (message: string, destination: string) => void
  ): StompSubscription {
    if (!this.client || !this.isConnected()) {
      throw new Error('Conexão STOMP não está disponível');
    }

    const destination = `/topic/${topicPattern}`;

    return this.client.subscribe(destination, (message: IMessage) => {
      callback(message.body, message.headers.destination || destination);
    });
  }

  public unsubscribe(subscription: StompSubscription): void {
    try {
      subscription.unsubscribe();
    } catch (error) {
      console.error('[RabbitStompService] Erro ao cancelar inscrição:', error);
    }
  }

  public on(
    event: keyof RabbitStompEventHandlers,
    handler: (...args: any[]) => void
  ): void {
    if (!this.eventHandlers[event].includes(handler)) {
      this.eventHandlers[event].push(handler);
    }
  }

  public off(
    event: keyof RabbitStompEventHandlers,
    handler: (...args: any[]) => void
  ): void {
    const handlers = this.eventHandlers[event];
    const index = handlers.indexOf(handler);
    if (index > -1) {
      handlers.splice(index, 1);
    }
  }

  public getClient(): Client | null {
    return this.client;
  }
}

export default RabbitStompService;

